/**
 * Strategies data layer (ARCHITECTURE.md §7 `/api/strategies/*`, §8).
 *
 * One place that binds the typed `hc<AppType>` client to TanStack Query: a query
 * key registry, `queryOptions`/`infiniteQueryOptions` factories the routes
 * prefetch in their loaders and components read with `useQuery`, and the small
 * set of mutation fetchers (lifecycle, dry-run toggle, export, import, preset
 * install). Every response funnels through {@link unwrap} so failures are uniform
 * `ApiError`s the global handler can react to.
 */
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type {
  BacktestDetail,
  CreateBacktest,
  CreateBacktestResponse,
  ImportStrategy,
  InstallPreset,
  ListBacktestsResponse,
  ListPresetsResponse,
  ListRunsResponse,
  ListStrategiesResponse,
  RunDecisionsResponse,
  Strategy,
  StrategyExport,
  StrategyPnl,
  StrategyStatus,
  UpdateStrategy,
} from "@richrich/contracts";
import { api, unwrap } from "../../lib/api";
import { keys } from "../../lib/queryKeys";
import { accountsQuery } from "../settings/api";

// ── reads ───────────────────────────────────────────────────────
/** `GET /api/strategies` — the full list, optionally filtered by status. */
export function strategyListQuery(status?: StrategyStatus) {
  return queryOptions({
    queryKey: keys.strategies.list(status),
    queryFn: async (): Promise<ListStrategiesResponse> =>
      unwrap<ListStrategiesResponse>(
        await api.strategies.$get({ query: status === undefined ? {} : { status } }),
      ),
  });
}

/** `GET /api/strategies/:id` — one strategy (definition + runtime bindings). */
export function strategyDetailQuery(id: string) {
  return queryOptions({
    queryKey: keys.strategies.detail(id),
    queryFn: async (): Promise<Strategy> =>
      unwrap<Strategy>(await api.strategies[":id"].$get({ param: { id } })),
  });
}

/** `GET /api/strategies/:id/pnl` — win-rate / payoff snapshot. */
export function strategyPnlQuery(id: string) {
  return queryOptions({
    queryKey: keys.strategies.pnl(id),
    queryFn: async (): Promise<StrategyPnl> =>
      unwrap<StrategyPnl>(await api.strategies[":id"].pnl.$get({ param: { id } })),
    staleTime: 30_000,
  });
}

/** One row of the strategy's ledger-scoped book (`null` = unknown, fail-closed). */
export interface StrategyPortfolioPosition {
  symbol: string;
  qty: string | null;
  sellableQty: string | null;
  positionValue: string | null;
  pnlPct: number | null;
}

/** `GET /api/strategies/:id/portfolio` — the ledger-scoped book the risk gate sees. */
export interface StrategyPortfolio {
  strategyId: string;
  dryRun: boolean;
  budget: { amount: string; currency: string };
  portfolioValue: string | null;
  allocatedValue: string | null;
  unrealizedPnl: string | null;
  realizedPnlToday: string | null;
  positions: StrategyPortfolioPosition[];
}

export function strategyPortfolioQuery(id: string) {
  return queryOptions({
    queryKey: keys.strategies.portfolio(id),
    queryFn: async (): Promise<StrategyPortfolio> =>
      unwrap<StrategyPortfolio>(await api.strategies[":id"].portfolio.$get({ param: { id } })),
    staleTime: 30_000,
  });
}

/** `GET /api/strategies/:id/runs` — keyset-paginated run history (newest first). */
export function strategyRunsInfiniteQuery(id: string) {
  return infiniteQueryOptions({
    queryKey: keys.strategies.runs(id),
    queryFn: async ({ pageParam }): Promise<ListRunsResponse> =>
      unwrap<ListRunsResponse>(
        await api.strategies[":id"].runs.$get({
          param: { id },
          query: pageParam === null ? { limit: "20" } : { limit: "20", cursor: pageParam },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** `GET /api/strategies/:id/runs/:runId/decisions` — the "why" of a single run. */
export function runDecisionsQuery(id: string, runId: string) {
  return queryOptions({
    queryKey: keys.strategies.decisions(id, runId),
    queryFn: async (): Promise<RunDecisionsResponse> =>
      unwrap<RunDecisionsResponse>(
        await api.strategies[":id"].runs[":runId"].decisions.$get({ param: { id, runId } }),
      ),
  });
}

/** `GET /api/strategies/presets` — the bundled preset catalog. */
export function presetsQuery() {
  return queryOptions({
    queryKey: keys.strategies.presets(),
    queryFn: async (): Promise<ListPresetsResponse> =>
      unwrap<ListPresetsResponse>(await api.strategies.presets.$get()),
    staleTime: 5 * 60_000,
  });
}

/**
 * `GET /api/accounts` — cached broker accounts, for the account-binding picker.
 * Settings owns this domain (§4.1 dedup — it also owns the sync mutation);
 * re-exported here so `AccountBinding.tsx` doesn't reach across features for it.
 * A Toss sync from settings still refreshes this via the shared `keys.accounts.list()`.
 */
export { accountsQuery };

// ── mutations ───────────────────────────────────────────────────
/** The lifecycle transitions exposed as `POST /api/strategies/:id/<action>`. */
export type LifecycleAction =
  | "activate"
  | "pause"
  | "resume"
  | "halt"
  | "reactivate"
  | "archive"
  | "unarchive";

/** Apply a lifecycle transition; the server re-arms/disarms the scheduler DO (§5). */
export async function applyLifecycle(id: string, action: LifecycleAction): Promise<Strategy> {
  const s = api.strategies[":id"];
  const res =
    action === "activate"
      ? await s.activate.$post({ param: { id } })
      : action === "pause"
        ? await s.pause.$post({ param: { id } })
        : action === "resume"
          ? await s.resume.$post({ param: { id } })
          : action === "halt"
            ? await s.halt.$post({ param: { id } })
            : action === "reactivate"
              ? await s.reactivate.$post({ param: { id } })
              : action === "archive"
                ? await s.archive.$post({ param: { id } })
                : await s.unarchive.$post({ param: { id } });
  return unwrap<Strategy>(res);
}

/** `PUT /api/strategies/:id` toggling only the per-strategy dry-run flag (§0.5). */
export async function setDryRun(id: string, dryRun: boolean): Promise<Strategy> {
  return unwrap<Strategy>(await api.strategies[":id"].$put({ param: { id }, json: { dryRun } }));
}

/**
 * `PUT /api/strategies/:id` binding an account and/or budget. This is what
 * satisfies the `transition()` guard on the server ("Bind an account before
 * activating this strategy.") — there was previously no UI path to it.
 */
export async function updateStrategy(id: string, patch: UpdateStrategy): Promise<Strategy> {
  return unwrap<Strategy>(await api.strategies[":id"].$put({ param: { id }, json: patch }));
}

/**
 * `DELETE /api/strategies/:id` — soft-delete an inactive strategy. Trade history
 * is preserved server-side; the server `409`s if the strategy is still active.
 */
export async function deleteStrategy(id: string): Promise<void> {
  await unwrap<{ ok: boolean }>(await api.strategies[":id"].$delete({ param: { id } }));
}

/** `GET /api/strategies/:id/export` — the portable, secret-free envelope (§0.6). */
export async function fetchStrategyExport(id: string): Promise<StrategyExport> {
  return unwrap<StrategyExport>(await api.strategies[":id"].export.$get({ param: { id } }));
}

/** `POST /api/strategies/import` — migrate + validate an envelope into a new draft. */
export async function importStrategyEnvelope(body: ImportStrategy): Promise<Strategy> {
  return unwrap<Strategy>(await api.strategies.import.$post({ json: body }));
}

/** `POST /api/strategies/presets/:presetId/install` — instantiate a preset as a draft. */
export async function installPreset(presetId: string, body: InstallPreset): Promise<Strategy> {
  return unwrap<Strategy>(
    await api.strategies.presets[":presetId"].install.$post({ param: { presetId }, json: body }),
  );
}

// ── backtests (§7 `/api/strategies/:id/backtests/*`) ─────────────
/** `GET /api/strategies/:id/backtests` — recent runs, newest first (headline-only). */
export function backtestListQuery(strategyId: string) {
  return queryOptions({
    queryKey: keys.strategies.backtests.list(strategyId),
    queryFn: async (): Promise<ListBacktestsResponse> =>
      unwrap<ListBacktestsResponse>(
        await api.strategies[":id"].backtests.$get({ param: { id: strategyId } }),
      ),
  });
}

/**
 * `GET /api/strategies/:id/backtests/:backtestId` — one run with its full result.
 * Polls every 1.5s while the job is `queued`/`running`, then stops (a settled run
 * is immutable, so there is nothing left to fetch). Pass `null` to keep the query
 * idle until a run is selected.
 */
export function backtestDetailQuery(strategyId: string, backtestId: string | null) {
  return queryOptions({
    queryKey: keys.strategies.backtests.detail(strategyId, backtestId ?? "∅"),
    queryFn: async (): Promise<BacktestDetail> =>
      unwrap<BacktestDetail>(
        await api.strategies[":id"].backtests[":backtestId"].$get({
          param: { id: strategyId, backtestId: backtestId as string },
        }),
      ),
    enabled: backtestId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "queued" || status === "running" ? 1500 : false;
    },
  });
}

/** `POST /api/strategies/:id/backtests` — enqueue a run; `202 { id }` on success. */
export async function createBacktest(
  strategyId: string,
  body: CreateBacktest,
): Promise<CreateBacktestResponse> {
  return unwrap<CreateBacktestResponse>(
    await api.strategies[":id"].backtests.$post({ param: { id: strategyId }, json: body }),
  );
}

/** `DELETE /api/strategies/:id/backtests/:backtestId` — remove a queued/errored run. */
export async function deleteBacktest(strategyId: string, backtestId: string): Promise<void> {
  await unwrap<{ ok: boolean }>(
    await api.strategies[":id"].backtests[":backtestId"].$delete({
      param: { id: strategyId, backtestId },
    }),
  );
}
